import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { CONTENT_TYPES, insightsApi } from "./api";
import { Breadcrumbs } from "../AdminLayout";
import { Plus, Search, ShieldCheck } from "lucide-react";
import { toast, Toaster } from "sonner";
import { formatErr } from "../api";

export default function ContentList() {
  const { type } = useParams();
  const cfg = CONTENT_TYPES[type];
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [pending, setPending] = useState([]);
  const nav = useNavigate();

  const load = () => {
    if (!cfg) return;
    const params = {};
    if (q) params.q = q;
    if (status) params.status = status;
    cfg.api.list(params).then(setRows).catch(e => toast.error(formatErr(e.response?.data?.detail)));
  };
  useEffect(load, [type, q, status, cfg]);
  useEffect(() => { insightsApi.pending().then(setPending).catch(()=>{}); }, [type]);

  if (!cfg) return <div className="p-8">Unknown content type.</div>;

  return (
    <div data-testid={`content-list-${type}`}>
      <Toaster richColors position="top-right"/>
      <Breadcrumbs items={["Admin", "Content", cfg.label]}/>
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-serif text-3xl text-navy tracking-tight">{cfg.label}</h1>
          <p className="text-stone2 text-sm mt-1">Create, edit, publish and schedule.</p>
        </div>
        <Link data-testid="new-btn" to={`/admin/content/${type}/new`} className="inline-flex items-center gap-2 bg-navy hover:bg-navy-hover text-white text-sm px-4 py-2">
          <Plus size={14}/> New {cfg.label.replace(/s$/,"")}
        </Link>
      </div>

      {pending.length > 0 && (
        <div data-testid="pending-approval-banner" className="mb-4 border border-[#D97706]/30 bg-[#D97706]/10 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-[#D97706] text-sm">
            <ShieldCheck size={14} strokeWidth={1.5}/> <span className="font-medium">{pending.length}</span> Clinical Insight{pending.length>1?"s":""} awaiting approval.
          </div>
          <Link to="/admin/insights-pending" className="mono text-[10px] uppercase tracking-widest text-[#D97706] hover:underline">Review →</Link>
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone2"/>
          <input data-testid="list-search" value={q} onChange={e=>setQ(e.target.value)} placeholder="Search…" className="w-full pl-9 pr-3 py-2 border border-line text-sm focus:outline-none focus:border-navy"/>
        </div>
        <select data-testid="list-status" value={status} onChange={e=>setStatus(e.target.value)} className="border border-line text-sm px-3 py-2">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
          <option value="published">Published</option>
          <option value="unpublished">Unpublished</option>
        </select>
      </div>

      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-clinical">
            <tr className="text-left mono text-[10px] uppercase tracking-widest text-stone2">
              <th className="px-4 py-3 border-b border-line">Title</th>
              <th className="px-4 py-3 border-b border-line">Slug / Category</th>
              <th className="px-4 py-3 border-b border-line">Status</th>
              <th className="px-4 py-3 border-b border-line">Insights</th>
              <th className="px-4 py-3 border-b border-line">Updated</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const insightsCount = (r.clinical_insights || []).length;
              return (
                <tr key={r.id} data-testid={`row-${r.id}`} onClick={()=>nav(`/admin/content/${type}/${r.id}`)}
                  className="border-b border-line last:border-0 hover:bg-clinical/30 cursor-pointer">
                  <td className="px-4 py-3 text-navy font-medium">{r[cfg.display] || r.title || r.name || r.slug || "—"}</td>
                  <td className="px-4 py-3">
                    <div className="mono text-xs text-stone2">{r.slug || "—"}</div>
                    <div className="text-xs text-stone2 mt-0.5">{r.category || r.issuing_body || ""}</div>
                  </td>
                  <td className="px-4 py-3"><span className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 ${statusChip(r.status)}`}>{(r.status||"draft").replace('_',' ')}</span></td>
                  <td className="px-4 py-3 text-xs text-stone2">{insightsCount || "—"}</td>
                  <td className="px-4 py-3 mono text-xs text-stone2">{r.updated_at?.slice(0,16).replace('T',' ')}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan="5" className="px-4 py-6 text-center text-stone2">No items.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function statusChip(s) {
  const m = {
    draft: "bg-line text-stone2",
    scheduled: "bg-[#D97706]/15 text-[#D97706]",
    published: "bg-[#3F6250]/10 text-[#3F6250]",
    unpublished: "bg-[#9F1239]/10 text-[#9F1239]",
  };
  return m[s] || m.draft;
}
